import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Bell, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import { POST_TYPES } from "@/constants/site";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { bnNum } from "@/constants/site";
import { useLang } from "@/lib/lang";

const agoBN = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "এইমাত্র";
  if (mins < 60) return `${bnNum(mins)} মিনিট আগে`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${bnNum(hrs)} ঘণ্টা আগে`;
  const days = Math.floor(hrs / 24);
  return `${bnNum(days)} দিন আগে`;
};

const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastRead, setLastRead] = useState(() => Number(localStorage.getItem("rls_last_read") || 0));
  const [pushState, setPushState] = useState("default");
  const { lang, t } = useLang();

  const ago = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    const num = (n) => (lang === "bn" ? bnNum(n) : n);
    if (mins < 1) return t("ago_now");
    if (mins < 60) return t("ago_min", { n: num(mins) });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t("ago_hr", { n: num(hrs) });
    const days = Math.floor(hrs / 24);
    return t("ago_day", { n: num(days) });
  };

  useEffect(() => {
    API.get("/notifications", { params: { limit: 12 } })
      .then(({ data }) => setItems(data))
      .catch(() => {});
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setPushState("unsupported");
    } else {
      setPushState(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const t = Date.now();
      localStorage.setItem("rls_last_read", String(t));
      setLastRead(t);
    }
  }, [open]);

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      setPushState(perm);
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.register("/push-sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await API.get("/push/config");
      if (!data.publicKey) {
        toast.error(t("push_toast_err"));
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
      await API.post("/push/subscribe", { subscription: sub.toJSON() });
      toast.success(t("push_toast_ok"));
    } catch (e) {
      toast.error(t("push_toast_err"));
    }
  };

  const unread = items.filter((n) => new Date(n.created_at).getTime() > lastRead).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell-btn"
          className="relative h-10 w-10 rounded-full border border-[#F2B134]/25 grid place-items-center text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60 transition-colors"
          aria-label="Notifications"
        >
          <Bell size={17} className="flame" />
          {unread > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[#D7261E] text-[10px] font-bold grid place-items-center text-[#FDF6EA]">
              {lang === "bn" ? bnNum(unread) : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-80 bg-[#1E0A0E] border-[#F2B134]/25 text-[#FDF6EA] p-2" data-testid="notifications-panel">
        <p className="px-3 py-2 text-xs uppercase tracking-[0.2em] text-[#F2B134]/80">{t("bell_title")}</p>
        {items.length === 0 && <p className="px-3 py-4 text-sm text-[#D1BBA7]/70">{t("bell_empty")}</p>}
        <div className="max-h-80 overflow-y-auto no-scrollbar">
          {items.map((n) => (
            <div key={n.id} className="px-3 py-3 rounded-lg hover:bg-[#F2B134]/8">
              <p className="text-sm font-semibold leading-snug">{n.title}</p>
              {n.body && <p className="text-xs text-[#D1BBA7]/80 mt-0.5 line-clamp-2">{n.body}</p>}
              <p className="text-[10px] text-[#F2B134]/60 mt-1">
                {(lang === "en" ? POST_TYPES[n.type]?.labelEN : POST_TYPES[n.type]?.label) || n.type} · {ago(n.created_at)}
              </p>
            </div>
          ))}
        </div>
        <div className="border-t border-[#F2B134]/15 mt-1 pt-1">
          {pushState === "granted" ? (
            <p className="px-3 py-2 text-xs text-[#F2B134] flex items-center gap-2" data-testid="push-status-on">
              <BellRing size={13} /> {t("push_on")}
            </p>
          ) : pushState === "unsupported" || pushState === "denied" ? (
            <p className="px-3 py-2 text-xs text-[#D1BBA7]/60 flex items-center gap-2" data-testid="push-status-off">
              <BellOff size={13} /> {pushState === "denied" ? t("push_denied") : t("push_unsupported")}
            </p>
          ) : (
            <button
              onClick={enablePush}
              data-testid="push-toggle-btn"
              className="w-full px-3 py-2 text-xs font-semibold text-[#F2B134] flex items-center gap-2 hover:bg-[#F2B134]/10 rounded-lg transition-colors"
            >
              <BellRing size={13} /> {t("push_enable")}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
